import { Component, OnDestroy, OnInit } from "@angular/core"
import { ActivatedRoute, Params } from "@angular/router"
import { Title } from "@angular/platform-browser"
import { Attribute } from "../../../models/attribute.model"
import { PremiumSectionPage } from "../../../models/collection/premiumSectionPage.model"
import { PremiumSectionTopic } from "../../../models/collection/premiumSectionTopic.model"
import { PremiumSubCollectionGTM } from "../../../models/gtm/collection"
import { CollectionService } from "../../../services/collection.service"
import { UserService } from "../../../services/user.service"
import { EduComponent } from "../../../utils/eduComponent"
import Utils from "../../../utils/utils"
import { GTMService } from "../../../services/gtm.service"
import { PremiumRelatedSection } from "../../../models/collection/premiumRelatedSection.model"
import { combineLatest, Observable, Subject } from "rxjs"
import { map, takeUntil, tap } from "rxjs/operators"
import { StudentGateService } from "../../../services/student-gate.service"
import { CollectionNode } from "../../../models/collectionNode.model"
import { HttpParams } from "@angular/common/http"
import { PreviewService } from "../../../services/preview.service"
import { routeAnimations, routeTransition } from "../../../utils/animations"

@Component({
    selector: "app-collection-premium-subcollection",
    templateUrl: "./collection-premium-subcollection.component.html",
    styleUrls: ["./collection-premium-subcollection.component.scss"],
    host: { "[@routeTransition]": "true", "[@routeAnimations]": "false", style: "display: block;" },
    animations: [routeTransition, routeAnimations],
    standalone: false,
})
export class CollectionPremiumSubcollectionComponent
    extends EduComponent
    implements OnInit, OnDestroy
{
    public section: PremiumSectionPage
    public footerAttributions: Attribute[]
    public innerWidth: number = window.innerWidth
    public cardTypeChangeBreakpoint: number = 664
    public premiumCollectionSlug: string = ""
    public premiumCollectionLogo: string = ""
    public subcollectionTitle: string = ""
    public premiumCollectionTitle: string = ""
    public isStudent: boolean
    public selectedGrade: string = ""
    public params: Params = new HttpParams()
    public readonly stickyNavbarHeight = 68

    private destroyed$: Subject<void> = new Subject<void>()

    studentToggleObservable: Observable<[string, string]> = combineLatest([
        this.studentGateService.collectionFilterChoice,
        this.studentGateService.selectedGradeState,
    ]).pipe(
        takeUntil(this.destroyed$),
        tap(([collectionFilter, selectedGrade]: [string, string]) => {
            this.selectedGrade = selectedGrade
            if (
                (collectionFilter.includes(selectedGrade) || collectionFilter === "All grades") &&
                this.premiumCollectionSlug
            )
                this.callSubCollectionChange()
        })
    )

    routeParamsObservable: Observable<any> = this.route.params.pipe(
        takeUntil(this.destroyed$),
        tap((params: Params) => {
            this.params = params
            this.callSubCollectionChange()
        })
    )

    collectionChange: Observable<void> = this.collectionService.activeSubCollectionsChange.pipe(
        takeUntil(this.destroyed$),
        map((response: CollectionNode) => this.setComponentData(response))
    )

    constructor(
        public collectionService: CollectionService,
        public gtmService: GTMService,
        public userService: UserService,
        private route: ActivatedRoute,
        private titleService: Title,
        private studentGateService: StudentGateService,
        private previewService: PreviewService
    ) {
        super()
    }

    ngOnInit() {
        this.studentToggleObservable.subscribe()
        this.routeParamsObservable.subscribe()
        this.isStudent = this.userService.hasStudentExperience()
        this.collectionChange.subscribe()
        this.innerWidth = window.innerWidth
        Utils.resize(() => {
            this.innerWidth = window.innerWidth
        })
    }

    private callSubCollectionChange() {
        this.collectionService.getPremiumSubcollectionData(
            this.params.slug,
            this.params.sectionURL,
            this.params.subcollectionURL
        )
    }

    setComponentData(response: CollectionNode) {
        if (!response.id) return
        this.section = PremiumSectionPage.fromData(response)
        this.previewService.subCollectionId = response.id
        this.previewService.subCollectionType = this.section.subcollectionType
        this.subcollectionTitle = this.section.title
        this.footerAttributions = this.section.getFooterAttributions()
        this.premiumCollectionSlug = this.section.parentCollectionSlug
        this.premiumCollectionLogo = this.section.parentCollectionWhiteLogo.itsURL
        this.premiumCollectionTitle = this.section.parentCollectionTitle
        this.titleService.setTitle(this.section.metaTitle)
        this.setGTM()
        this.collectionService.incrementSubcollectionViewCount(
            this.section.subcollectionId,
            this.section.subcollectionType
        )
        this.emitAdminHeaderLinks()
        this.scrollTopicIntoView()
    }

    setGTM() {
        this.gtmService.resetDataLayer()
        this.gtmService.setDataLayer(new PremiumSubCollectionGTM(this.section))
        this.gtmService.pushPageView()
    }
    showMoreRelated($event, relatedSection: PremiumRelatedSection) {
        $event.preventDefault()
        relatedSection.showMore()
    }

    showLessRelated($event, relatedSection: PremiumRelatedSection) {
        $event.preventDefault()
        relatedSection.showLess()
    }

    buildCssSelector(text: string) {
        return Utils.buildCssSelector(text)
    }

    showMore($event, topic: PremiumSectionTopic) {
        $event.preventDefault()
        this.collectionService.showMoreTopicResources(topic)
    }

    showLess($event, topic: PremiumSectionTopic) {
        $event.preventDefault()
        topic.showLessResources()
    }

    private emitAdminHeaderLinks(): void {
        if (!this.section) return
        const { url: adminLink, title } = this.section.getAdminLink()

        this.userService.emitAdminHeaderData({
            title,
            adminLink,
            isPublished: this.section.parentPublish,
        })
    }

    private scrollTopicIntoView() {
        // We need this timeout in order to make sure everything is rendered before scrolling
        setTimeout(() => {
            const fragment = this.route.snapshot.fragment
            if (!fragment) {
                return
            }

            const topic = this.section.topics.find(
                (topic) => topic?.titleForAutoscroll === fragment
            )
            const element = document.getElementById(`section-${topic?.titleForAutoscroll}`)

            if (element) {
                Utils.scrollIntoView(element, -this.stickyNavbarHeight + 2)
            }
        }, 400)
    }

    ngOnDestroy() {
        super.ngOnDestroy()
        this.userService.emitAdminHeaderData({})
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
